import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Clock, AlertTriangle, Sparkles, Crown } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPlanBadgeInfo } from "@/lib/billingPlans";

interface PlanBadgeProps {
  planType: string | null | undefined;
  trialEndsAt?: string | null;
  showTooltip?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

export function PlanBadge({ 
  planType, 
  trialEndsAt, 
  showTooltip = true, 
  size = 'md',
  className 
}: PlanBadgeProps) {
  const badgeInfo = getPlanBadgeInfo(planType, trialEndsAt);
  
  const iconSize = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  
  const IconComponent = () => {
    if (!badgeInfo.showIcon || !badgeInfo.iconType) return null;
    
    switch (badgeInfo.iconType) {
      case 'clock':
        return <Clock className={iconSize} />;
      case 'warning':
        return <AlertTriangle className={iconSize} />;
      case 'sparkles':
        return <Sparkles className={iconSize} />;
      case 'crown':
        return <Crown className={iconSize} />;
      default:
        return null;
    }
  };

  const badge = (
    <Badge 
      variant={badgeInfo.variant}
      className={cn(
        "flex items-center gap-1 cursor-default",
        size === 'sm' && 'text-xs py-0 px-1.5',
        badgeInfo.className,
        className
      )}
    >
      <IconComponent />
      {badgeInfo.label}
    </Badge>
  );

  if (!showTooltip) {
    return badge;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badge}
        </TooltipTrigger>
        <TooltipContent>
          <p>{badgeInfo.tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
